import { inject, Injectable } from "@angular/core";
import { mutationOptions, QueryClient, queryOptions } from "@tanstack/angular-query-experimental";
import { lastValueFrom } from "rxjs";

import { AWSHttpClient } from "@intocare/aws";
import { DataBundle } from "@intocare/datatypes/lib/bundle";
import { SupportedLocale } from "@intocare/datatypes/lib/i18n";
import { ValueSet } from "@intocare/datatypes/lib/value-set";

import { CareTreeItemDto, careTreeValueSetFor } from "./care-tree.dto";

export enum MedicationResponsible {
	RESIDENT = "RESIDENT",
	ORGANISATION = "ORGANISATION",
}

export enum MedicationResponsibleType {
	PRESCRIPTION = "PRESCRIPTION",
	ORDER = "ORDER",
	ADMINISTRATION = "ADMINISTRATION",
}

export interface ResidentMedicationSettings {
	isPrescriptionRequired: boolean;
	isMultidoseForbidden?: boolean;
	prescriptionResponsible?: MedicationResponsible;
	orderResponsible?: MedicationResponsible;
	administrationResponsible?: MedicationResponsible;
	administrationVerification?: boolean;
}

export interface MedicationSettings {
	isPrescriptionRequired: boolean;
	isMultidoseForbidden: boolean;
	resident: MedicationResponsibleType[];
	organisation: MedicationResponsibleType[];
	administrationResponsible?: undefined;
	administrationVerification?: boolean;
}

export enum PosologyType {
	AD_HOC = "AD_HOC",
	ONE_TIME = "ONE_TIME",
	REPEATING = "REPEATING",
	CYCLIC = "CYCLIC",
}

export enum PosologyEndType {
	END_BOX = "END_BOX",
	DATE = "DATE",
	INFINITY = "INFINITY",
}

export type I18n = {
	nl?: string;
	fr?: string;
};

export interface AdministrationUnit {
	id: string;
	name: I18n | string;
}

export enum PosologyAdministerRepeatInterval {
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	MONTHLY = "MONTHLY",
	YEARLY = "YEARLY",
}

export interface AdministrationDetail {
	id?: string;
	quantity: number;
	meal?: string;
	mealLink?: string;
	remarks?: string;
	day?: number;
	cycleDay?: number;
	administrationMoment?: string;
	beforeTimeOffset?: string;
	repeatInterval?: PosologyAdministerRepeatInterval;
	repeatIntervalValue?: number;
	administrationUnit?: AdministrationUnit;
	sober?: boolean;
}

export enum PosologyStatus {
	ACTIVE = "ACTIVE",
	STOPPED = "STOPPED",
	EXPIRED = "EXPIRED",
}

export enum PosologyDurationTime {
	ALL = "ALL",
	CHRONIC = "CHRONIC",
	ACUTE = "ACUTE",
}

interface Restrictions {
	maximumDoseQuantity: number;
	administrationInterval?: number;
	maximumDailyQuantity?: number;
	minimumDoseQuantity?: number;
}

export interface Medication {
	id: string;
	code: string;
	codeType: string;
	name: I18n;
	isStockTrackable?: boolean;
	administrationUnit?: string;
	administrationUnits?: string[];
	dividable?: DividableType;
}

export interface PosologySubstitution {
	medicationId: string;
	multiplier: number;
	posologyId?: string;
	medication?: Medication;
}

export interface Posology {
	id?: string;
	patient?: string;
	type: PosologyType;
	startDate?: string;
	medicationId?: string;
	durationType?: PosologyEndType;
	endDate?: string;
	administrationDetails?: AdministrationDetail[];
	remarks?: string;
	status?: PosologyStatus;
	durationTime?: PosologyDurationTime;
	administrationReason?: string;
	adHocAdministrationUnit?: AdministrationUnit;
	restrictions?: Restrictions;
	administrationUnit?: AdministrationUnit;
	days?: number[];
	cycleDays?: number | string;
	cycleStartDate?: string;
	stoppedDate?: string;
	totalAdministerQuantity?: number;
	substitutionTo?: PosologySubstitution;
	substitutionFrom?: PosologySubstitution;
	isRecentlyUpdated?: boolean;
}

export enum CustomProductDistributionType {
	ROBOT = "ROBOT",
	MANUAL = "MANUAL",
}

export enum DividableType {
	TWO = "2",
	FOUR = "4",
	// Uneven
	N = "N",
	// Not dividable
	X = "X",
}

export interface ResidentMedication {
	patient: string;
	medicationId: string;
	medicationStatus: string;
	name: I18n;
	searchName?: string;
	multidose?: boolean;
	type?: string;
	frequency?: string[];
	beginDate?: string;
	endType?: string;
	endDate?: string;
	blocked?: boolean;
	stopped?: boolean;
	posologies?: Posology[];
	chronic?: Posology[];
	acute?: Posology[];
	administrationUnits?: AdministrationUnit[];
	administrationUnit?: AdministrationUnit;
	blockReason?: I18n;
	code?: string;
	distributionType?: CustomProductDistributionType;
	medicationSettings?: ResidentMedicationSettings | MedicationSettings;
	isStockTrackable: boolean;
	dividable?: DividableType;
	isArchived?: boolean;
	posologyCount: number;
	highRisk?: boolean;
}

export interface MedicationGroup {
	id: string;
	icon: string;
	color: string;
	name: I18n;
	medications: ResidentMedication[];
	allowZeroValues?: boolean;
}

export interface MedicationSchemaGroupedByDistributionTypeDto {
	robotSchema: MedicationGroup[];
	manualSchema: MedicationGroup[];
}

export const legacyQueryKeys = {
	base() {
		return ["legacy"] as const;
	},
	medicationSettingsByResidentId(params: { residentId: string }) {
		return [...this.base(), "medication-settings", "residentId", params.residentId] as const;
	},
	medicationSchemaGroupedByDistributionTypeByResidentId(params: { residentId: string; all?: boolean }) {
		return [...this.base(), "medication-schema-grouped-by-distribution-type", params] as const;
	},
	careTreeValueSet(params: { locale: SupportedLocale; filters?: { active?: true } }) {
		return [...this.base(), "care-tree", "value-set", params.locale, params.filters];
	},
};

@Injectable({
	providedIn: "root",
})
export class LegacyDataAccess {
	private http = inject(AWSHttpClient);
	private queryClient = inject(QueryClient);

	public getMedicationSettingsForResidentQueryOptions = (residentId: string) => {
		return queryOptions({
			queryKey: legacyQueryKeys.medicationSettingsByResidentId({ residentId }),
			queryFn: () => lastValueFrom(this.http.get<ResidentMedicationSettings>(`/medication/${residentId}/settings`)),
			enabled: !!residentId,
		});
	};

	public getUpdateResidentMedicationSettingsMutationOptions = (residentId: string) => {
		return mutationOptions({
			mutationFn: (settings: ResidentMedicationSettings) =>
				lastValueFrom(this.http.put<ResidentMedicationSettings>(`/medication/${residentId}/settings`, settings)),
		});
	};

	public getUpdateMedicationSettingsMutationOptions = (residentId: string, medicationId: string) => {
		return mutationOptions({
			mutationFn: (settings: MedicationSettings) =>
				lastValueFrom(this.http.put<MedicationSettings>(`medication/${residentId}/${medicationId}`, settings)),
		});
	};

	public getMedicationSchemaGroupedByDistributionTypeForResidentQueryOptions = (residentId: string, all: boolean) => {
		return queryOptions({
			queryKey: legacyQueryKeys.medicationSchemaGroupedByDistributionTypeByResidentId({ residentId, all }),
			queryFn: () => {
				return lastValueFrom(
					this.http.get<MedicationSchemaGroupedByDistributionTypeDto>(
						`/medication/${residentId}/schema-grouped-by-distribution-type`,
						{ params: { all } },
					),
				);
			},
			enabled: !!residentId,
			staleTime: Infinity,
			gcTime: 0,
		});
	};

	public async invalidateMedicationSchemaGroupedByDistributionType(residentId: string) {
		await this.queryClient.invalidateQueries({
			queryKey: legacyQueryKeys.medicationSchemaGroupedByDistributionTypeByResidentId({ residentId }),
		});
	}

	careTreeValueSetQueryOptions(params: { locale: SupportedLocale; filters?: { active?: true } }) {
		return queryOptions<ValueSet>({
			queryKey: legacyQueryKeys.careTreeValueSet(params),
			queryFn: () =>
				lastValueFrom(this.http.get<DataBundle<CareTreeItemDto[]>>("/caretree")).then(careTreeValueSetFor(params)),
		});
	}
}
